/**
 * MyPromisePage — Обещание Nutramilo (iter1117)
 *
 * Личен преглед на поведенческия ангажимент. Показва:
 *   • Пълнота на здравния профил (0–100 %) по панели.
 *   • Непрекъсната активност (брой последователни седмици).
 *   • Остатъчни дни до изтичане на ангажимента.
 *   • Състояние на напредъка спрямо целите.
 *
 * Езикът е български литературен, без разговорни или заети изрази.
 * Нищо на страницата не представлява медицинско твърдение — тя мери
 * само поведенчески показатели (въвеждане на данни и активност).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { MobileTopBar } from "@/components/MobileTopBar";
import { Button } from "@/components/ui/button";
import { Loader2, Target, Clock, CheckCircle2, XCircle, Flag } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUS_LABEL = {
  active:    { label: "Активен",    color: "#0EA5E9" },
  achieved:  { label: "Постигнат",  color: "#10B981" },
  failed:    { label: "Непостигнат", color: "#EF4444" },
  cancelled: { label: "Отказан",    color: "#64748B" },
  refunded:  { label: "Възстановен", color: "#8B5CF6" },
};

function daysBetween(fromIso, toIso) {
  const a = new Date(fromIso).getTime();
  const b = new Date(toIso).getTime();
  return Math.max(0, Math.ceil((b - a) / (1000 * 60 * 60 * 24)));
}

export default function MyPromisePage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/nutramilo-promise/me`);
      setData(r.data);
    } catch (e) {
      toast.error("Неуспешно зареждане на ангажимента.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const enroll = async () => {
    if (!acceptTerms) {
      toast.error("Моля, приемете условията, за да се включите.");
      return;
    }
    setWorking(true);
    try {
      await axios.post(`${API}/nutramilo-promise/enroll`, { accept_terms: true });
      toast.success("Включихте се успешно.");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Неуспешно включване.";
      toast.error(detail);
    } finally {
      setWorking(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm(
      "Сигурни ли сте, че искате да откажете активния ангажимент? " +
      "Отказът НЕ дава право на възстановяване на платена сума."
    )) return;
    setWorking(true);
    try {
      await axios.post(`${API}/nutramilo-promise/cancel`);
      toast.success("Ангажиментът е отказан.");
      await load();
    } catch (e) {
      toast.error("Неуспешен отказ.");
    } finally {
      setWorking(false);
    }
  };

  const bg = isDark ? "#0B0F19" : "#F8FAFC";
  const card = isDark ? "#111827" : "#FFFFFF";
  const text = isDark ? "#E2E8F0" : "#0F172A";
  const muted = isDark ? "#94A3B8" : "#475569";
  const border = isDark ? "#1F2937" : "#E2E8F0";

  const metrics = data?.metrics || {};
  const comp = metrics.completeness || { percent: 0, filled_count: 0, total_count: 35, by_panel: {} };
  const streak = metrics.streak || { weeks: 0, active_this_week: false };
  const settings = data?.settings || { target_percent: 80, target_weeks: 8, duration_days: 90, refund_amount: 100, currency: "BGN" };
  const promise = data?.promise || null;
  const active = data?.has_active_promise;

  const daysLeft = useMemo(() => {
    if (!promise) return null;
    return daysBetween(new Date().toISOString(), promise.ends_at);
  }, [promise]);

  if (loading) {
    return (
      <div style={{ background: bg, minHeight: "100vh", color: text }}>
        <MobileTopBar />
        <div className="flex items-center justify-center" style={{ padding: 80 }}>
          <Loader2 className="animate-spin" size={32} />
        </div>
      </div>
    );
  }

  const pctOnTrack = comp.percent >= settings.target_percent;
  const weeksOnTrack = streak.weeks >= settings.target_weeks;
  const bothOnTrack = pctOnTrack && weeksOnTrack;

  return (
    <div style={{ background: bg, minHeight: "100vh", color: text, paddingBottom: 60 }} data-testid="my-promise-page">
      <MobileTopBar />
      <div style={{ maxWidth: 920, margin: "0 auto", padding: "24px 16px" }}>
        {/* Заглавие */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
            Обещание Nutramilo
          </h1>
          <p style={{ color: muted, fontSize: 15, lineHeight: 1.6 }}>
            Поведенчески ангажимент — не медицинско обещание. Ако до края
            на срока постигнете посочените цели за въвеждане на данни и
            непрекъсната активност, получавате право на частично
            възстановяване на платената абонаментна сума.
          </p>
        </div>

        {/* Карта: Цели */}
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Target size={18} />
            <strong>Цели на ангажимента</strong>
          </div>
          <ul style={{ listStyle: "disc", paddingLeft: 20, color: muted, fontSize: 14, lineHeight: 1.8 }}>
            <li>Пълнота на здравния профил поне <strong style={{ color: text }}>{settings.target_percent} %</strong></li>
            <li>Непрекъсната активност поне <strong style={{ color: text }}>{settings.target_weeks}</strong> последователни седмици</li>
            <li>Срок за постигане: <strong style={{ color: text }}>{settings.duration_days}</strong> дни</li>
            <li>Награда при постигане: възстановяване на <strong style={{ color: text }}>{settings.refund_amount} {settings.currency}</strong> от платената абонаментна сума</li>
          </ul>
        </div>

        {/* Карта: Напредък */}
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <strong>Текущ напредък</strong>
            {active && daysLeft !== null && (
              <span style={{ fontSize: 13, color: muted, display: "flex", alignItems: "center", gap: 4 }}>
                <Clock size={14} /> още {daysLeft} дни
              </span>
            )}
          </div>

          <ProgressRow
            label="Пълнота на здравния профил"
            value={comp.percent}
            target={settings.target_percent}
            unit="%"
            detail={`${comp.filled_count} от ${comp.total_count} въведени маркера`}
            onTrack={pctOnTrack}
            text={text}
            muted={muted}
            testid="promise-completeness"
          />

          <ProgressRow
            label="Непрекъсната активност"
            value={streak.weeks}
            target={settings.target_weeks}
            unit=" седм."
            detail={streak.active_this_week ? "Активни тази седмица" : "Няма активност тази седмица"}
            onTrack={weeksOnTrack}
            text={text}
            muted={muted}
            testid="promise-streak"
          />

          {active && (
            <div style={{
              marginTop: 16, padding: 12, borderRadius: 8,
              background: bothOnTrack ? "#DCFCE7" : "#FEF3C7",
              color: bothOnTrack ? "#166534" : "#92400E",
              fontSize: 14,
            }}>
              {bothOnTrack
                ? "Към момента покривате и двете цели. Продължете активността до края на срока, за да заключите наградата."
                : "Все още не покривате и двете цели. Качете още лабораторни резултати и влизайте поне веднъж седмично."}
            </div>
          )}
        </div>

        {/* Карта: Пълнота по панели */}
        {Object.keys(comp.by_panel || {}).length > 0 && (
          <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <strong style={{ display: "block", marginBottom: 12 }}>Пълнота по панели</strong>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
              {Object.entries(comp.by_panel).map(([panel, s]) => (
                <div key={panel} style={{ padding: 10, border: `1px solid ${border}`, borderRadius: 8 }} data-testid={`panel-${panel}`}>
                  <div style={{ fontSize: 13, color: muted, marginBottom: 4 }}>{panel}</div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>
                    {s.filled} / {s.total}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Статус на текущия ангажимент + бутони */}
        {active && promise ? (
          <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <Flag size={18} />
              <strong>Състояние на ангажимента</strong>
              <span style={{
                padding: "2px 10px", borderRadius: 999, fontSize: 12,
                background: (STATUS_LABEL[promise.status]?.color || "#64748B") + "22",
                color: STATUS_LABEL[promise.status]?.color || "#64748B",
              }}>
                {STATUS_LABEL[promise.status]?.label || promise.status}
              </span>
            </div>
            <div style={{ color: muted, fontSize: 14, marginBottom: 12 }}>
              Включен на: {new Date(promise.started_at).toLocaleDateString("bg-BG")}
              {" · "}
              Изтича на: {new Date(promise.ends_at).toLocaleDateString("bg-BG")}
            </div>
            <Button
              variant="outline"
              onClick={cancel}
              disabled={working}
              data-testid="promise-cancel-btn"
            >
              Откажи ангажимента
            </Button>
          </div>
        ) : promise && !active ? (
          <ClosedPromiseCard promise={promise} card={card} border={border} muted={muted} />
        ) : (
          <EnrollCard
            card={card}
            border={border}
            muted={muted}
            text={text}
            settings={settings}
            acceptTerms={acceptTerms}
            setAcceptTerms={setAcceptTerms}
            working={working}
            onEnroll={enroll}
          />
        )}

        <div style={{ marginTop: 24, fontSize: 12, color: muted, lineHeight: 1.6 }}>
          Nutramilo е образователна платформа. Показателите тук отразяват
          активността Ви в приложението и броя въведени лабораторни
          стойности. Те не представляват диагностична или медицинска
          преценка.
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────── */
function ProgressRow({ label, value, target, unit, detail, onTrack, text, muted, testid }) {
  const pct = Math.min(100, (value / target) * 100);
  return (
    <div style={{ marginBottom: 14 }} data-testid={testid}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 6 }}>
        <span>{label}</span>
        <span style={{ color: onTrack ? "#10B981" : text, fontWeight: 600 }}>
          {value}{unit}
          <span style={{ color: muted, fontWeight: 400, fontSize: 12 }}>  / {target}{unit}</span>
          {onTrack && <CheckCircle2 size={14} style={{ display: "inline", marginLeft: 6, color: "#10B981" }} />}
        </span>
      </div>
      <div style={{ height: 8, background: "#1E293B22", borderRadius: 999, overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: `${pct}%`,
          background: onTrack ? "#10B981" : "#0EA5E9",
          transition: "width 400ms ease",
        }} />
      </div>
      <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>{detail}</div>
    </div>
  );
}


function EnrollCard({ card, border, muted, text, settings, acceptTerms, setAcceptTerms, working, onEnroll }) {
  return (
    <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: 20 }}>
      <strong style={{ display: "block", marginBottom: 10 }}>Включване в ангажимента</strong>
      <p style={{ color: muted, fontSize: 14, lineHeight: 1.6, marginBottom: 14 }}>
        При включване се задава срок от {settings.duration_days} дни. Ако до края
        достигнете поне {settings.target_percent} % пълнота на здравния профил
        и поне {settings.target_weeks} последователни активни седмици, получавате
        право на възстановяване на {settings.refund_amount} {settings.currency}
        от платената абонаментна сума. Отказът преди края не дава право на
        възстановяване.
      </p>
      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, color: text, marginBottom: 14 }}>
        <input
          type="checkbox"
          checked={acceptTerms}
          onChange={(e) => setAcceptTerms(e.target.checked)}
          data-testid="promise-accept-terms"
          style={{ marginTop: 4 }}
        />
        <span>
          Приемам условията на ангажимента. Разбирам, че показателите са
          поведенчески и не представляват клинично обещание.
        </span>
      </label>
      <Button
        onClick={onEnroll}
        disabled={!acceptTerms || working}
        data-testid="promise-enroll-btn"
      >
        {working ? "Моля изчакайте…" : "Включвам се"}
      </Button>
    </div>
  );
}


function ClosedPromiseCard({ promise, card, border, muted }) {
  const Icon = promise.status === "achieved" || promise.status === "refunded"
    ? CheckCircle2
    : XCircle;
  const color = STATUS_LABEL[promise.status]?.color || "#64748B";
  return (
    <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <Icon size={20} color={color} />
        <strong>Приключил ангажимент</strong>
        <span style={{
          padding: "2px 10px", borderRadius: 999, fontSize: 12,
          background: color + "22", color,
        }}>
          {STATUS_LABEL[promise.status]?.label || promise.status}
        </span>
      </div>
      <div style={{ color: muted, fontSize: 14 }}>
        Приключи на: {promise.settled_at
          ? new Date(promise.settled_at).toLocaleDateString("bg-BG")
          : "—"}
        {promise.refunded_at && (
          <> · Възстановено на: {new Date(promise.refunded_at).toLocaleDateString("bg-BG")}</>
        )}
      </div>
    </div>
  );
}
